import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayer from 'react-player/youtube';
import StyledImage from '@apps/components/Image';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Box, IconButton } from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';

type VideoImagePopperProps = {
  videoUrl?: string;
};

const VideoImagePopper: React.FC<VideoImagePopperProps> = ({ videoUrl }) => {
  const [open, setOpen] = useState(false);

  // ======================== HOOKS

  const { sm: isSm, md: isMd } = useGetResponsive();

  // ======================== EVENT HANDLERS
  const onHandleClose = () => {
    setOpen(false);
  };

  const onHandleOpen = () => {
    setOpen(true);
  };

  // ======================== VARIABLES
  const getVideoIdFromUrl = (url?: string) => {
    if (url) {
      let videoId = url.split('v=')[1];
      if (videoId.includes('&')) {
        videoId = videoId.substring(0, videoId.indexOf('&'));
      }
      return videoId;
    }
    return '';
  };

  // =========================== RENDER FUNCTION
  const renderPopper = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100%',
          flexDirection: 'column',
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <IconButton
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            right: '0',
            top: '0',
            cursor: 'pointer',
            height: '2.75rem',
            width: '2.75rem',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '1',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            '&:hover .arrowColor': { color: '#FFC800' },
            '&:hover': { opacity: 1, backgroundColor: '#FDFDFD' },
          }}
          onClick={onHandleClose}
        >
          <CloseIcon className="arrowColor" sx={{ color: '#000' }} />
        </IconButton>
        <ReactPlayer
          url={videoUrl}
          light={true}
          width="70%"
          height="70%"
          controls={true}
          playing={true}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          pb: isSm ? '2.5rem' : isMd ? '2rem' : '',
        }}
        onClick={onHandleOpen}
      >
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            objectFit: 'cover',
            overflow: 'hidden',
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        >
          <StyledImage
            src={`https://img.youtube.com/vi/${getVideoIdFromUrl(
              videoUrl
            )}/sddefault.jpg`}
            width={278}
            height={130}
          />
          <PlayCircleOutlineIcon
            sx={{
              color: 'white',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)', // Centering the icon
              fontSize: '3rem',
              cursor: 'pointer',
            }}
          />
        </Box>
      </Box>

      {open && ReactDOM.createPortal(renderPopper(), document.body)}
    </>
  );
};

export default VideoImagePopper;
